<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <span>Edit Prescription Item</span>
        </v-card-title>
        <v-card-text>
          <div v-if="isLoading" class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <p class="mt-2">Loading prescription item...</p>
          </div>
          <div v-else-if="!prescriptionItem && !isLoading" class="text-center">
            <v-icon large color="error">mdi-alert-circle</v-icon>
            <p class="mt-2">Prescription Item not found</p>
            <v-btn color="primary" @click="$router.back()">Go Back</v-btn>
          </div>
          <div v-else>
            <v-row>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.prescription_id"
                  :items="prescriptions"
                  :item-text="getPrescriptionDisplayText"
                  item-value="id"
                  solo
                  rounded
                  label="Select Prescription"
                  prepend-inner-icon="mdi-file-document"
                  :error-messages="errors.prescription_id"
                  clearable
                  required
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.product_id"
                  :items="products"
                  :item-text="getProductDisplayText"
                  item-value="id"
                  solo
                  rounded
                  label="Select Product"
                  prepend-inner-icon="mdi-pill"
                  :error-messages="errors.product_id"
                  clearable
                  required
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.mode_id"
                  :items="modes"
                  item-text="name"
                  item-value="id"
                  solo
                  rounded
                  label="Administration Mode (Optional)"
                  prepend-inner-icon="mdi-medical-bag"
                  :error-messages="errors.mode_id"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-text-field
                  v-model="form.dosage"
                  solo
                  rounded
                  label="Dosage (mg)"
                  prepend-inner-icon="mdi-weight"
                  type="number"
                  min="0"
                  step="0.01"
                  :error-messages="errors.dosage"
                  placeholder="e.g., 500"
                ></v-text-field>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.frequency"
                  :items="frequencyOptions"
                  solo
                  rounded
                  label="Frequency"
                  prepend-inner-icon="mdi-clock-outline"
                  :error-messages="errors.frequency"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-autocomplete
                  v-model="form.duration"
                  :items="durationOptions"
                  solo
                  rounded
                  label="Duration"
                  prepend-inner-icon="mdi-calendar-range"
                  :error-messages="errors.duration"
                  clearable
                ></v-autocomplete>
              </v-col>
              <v-col sm="6">
                <v-switch
                  class="mt-0 ml-5"
                  v-model="form.is_active"
                  inset
                  :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                ></v-switch>
              </v-col>
              <v-col sm="12">
                <v-textarea
                  v-model="form.notes"
                  solo
                  rounded
                  label="Instructions/Notes"
                  rows="4"
                  prepend-inner-icon="mdi-note-text"
                  :error-messages="errors.notes"
                  placeholder="Enter special instructions, warnings, or additional notes..."
                ></v-textarea>
              </v-col>
            </v-row>

            <!-- Prescription Item Information Card -->
            <v-card class="mt-4" outlined>
              <v-card-title>
                <v-icon left>mdi-information</v-icon>
                Prescription Item Information
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col sm="3">
                    <strong>Created:</strong><br />
                    {{ formatDate(prescriptionItem.created_at) }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Last Updated:</strong><br />
                    {{ formatDate(prescriptionItem.updated_at) }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Created By:</strong><br />
                    {{
                      prescriptionItem.created_by
                        ? prescriptionItem.created_by.user_name
                        : "N/A"
                    }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Current Status:</strong><br />
                    <v-chip
                      small
                      :color="form.is_active ? 'teal' : 'red'"
                      text-color="white"
                    >
                      {{ form.is_active ? "Active" : "Inactive" }}
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row class="mt-3" v-if="prescriptionItem.prescription">
                  <v-col sm="6">
                    <strong>Prescription Details:</strong><br />
                    <small>
                      Doctor:
                      {{
                        prescriptionItem.prescription.doctor
                          ? prescriptionItem.prescription.doctor.user_name
                          : "N/A"
                      }}<br />
                      Patient:
                      {{
                        prescriptionItem.prescription.patient
                          ? prescriptionItem.prescription.patient.user_name
                          : "N/A"
                      }}
                    </small>
                  </v-col>
                  <v-col sm="6">
                    <strong>Product Details:</strong><br />
                    <small>
                      Product:
                      {{
                        prescriptionItem.product
                          ? prescriptionItem.product.name
                          : "N/A"
                      }}<br />
                      Category:
                      {{ prescriptionItem.product?.category || "N/A" }}
                    </small>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-divider class="mt-5 mb-5"></v-divider>
            <v-row>
              <v-col sm="12">
                <v-btn
                  :loading="isSaving"
                  :disabled="isSaving"
                  color="primary"
                  @click="updatePrescriptionItem"
                  class="mr-4"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Update Prescription Item
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="$router.back()"
                  :disabled="isSaving"
                  class="mr-4"
                >
                  <v-icon left>mdi-cancel</v-icon>
                  Cancel
                </v-btn>
                <v-btn
                  color="error"
                  @click="confirmDelete"
                  :disabled="isSaving"
                  outlined
                >
                  <v-icon left>mdi-delete</v-icon>
                  Delete Prescription Item
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Prescription Item updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-alert-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Confirm Delete</h1>
                      <br />
                      <h3>
                        Are you sure you want to delete this prescription item?
                      </h3>
                      <br />
                      <v-btn
                        color="error"
                        @click="deletePrescriptionItem"
                        :loading="isDeleting"
                        class="mr-4"
                      >
                        Yes, Delete
                      </v-btn>
                      <v-btn
                        color="secondary"
                        @click="showDeleteDialog = false"
                        :disabled="isDeleting"
                      >
                        Cancel
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PrescriptionItemEdit",
  data() {
    return {
      prescriptionItem: null,
      isSaving: false,
      isDeleting: false,
      isLoading: true,
      showSuccessDialog: false,
      showDeleteDialog: false,
      prescriptions: [],
      products: [],
      modes: [],
      errors: {},

      form: {
        prescription_id: null,
        product_id: null,
        mode_id: null,
        dosage: null,
        frequency: "",
        duration: "",
        notes: "",
        is_active: true,
      },

      frequencyOptions: [
        "once daily",
        "twice daily",
        "three times daily",
        "four times daily",
        "every 6 hours",
        "every 8 hours",
        "every 12 hours",
        "as needed",
        "before meals",
        "after meals",
        "at bedtime",
        "once weekly",
        "twice weekly",
      ],

      durationOptions: [
        "3 days",
        "5 days",
        "7 days",
        "10 days",
        "14 days",
        "21 days",
        "30 days",
        "6 weeks",
        "8 weeks",
        "3 months",
        "as directed",
        "until finished",
      ],
    };
  },

  mounted() {
    this.fetchPrescriptionItem();
    this.getMasters();
  },

  methods: {
    async getMasters() {
      try {
        const response = await axios.get("masters?context=prescription_items");

        if (response.data?.prescriptions) {
          this.prescriptions = response.data.prescriptions;
        }

        if (response.data?.products) {
          this.products = response.data.products;
        }

        if (response.data?.modes) {
          this.modes = response.data.modes.map(mode => ({
            id: mode.id,
            name: mode.description,
            value: mode.id,
            text: mode.description
          }));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    async fetchPrescriptionItem() {
      this.isLoading = true;
      try {
        const prescriptionItemId = this.$route.params.id;
        const response = await axios.get(
          `/prescription_items/${prescriptionItemId}`
        );

        if (response.data.success) {
          this.prescriptionItem = response.data.data;
          this.form = {
            prescription_id: this.prescriptionItem.prescription_id,
            product_id: this.prescriptionItem.product_id,
            mode_id: this.prescriptionItem.mode_id,
            dosage: this.prescriptionItem.dosage,
            frequency: this.prescriptionItem.frequency || "",
            duration: this.prescriptionItem.duration || "",
            notes: this.prescriptionItem.notes || "",
            is_active: this.prescriptionItem.is_active,
          };
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Prescription Item not found",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Prescription Item not found",
          });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error loading prescription item data",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    getPrescriptionDisplayText(prescription) {
      if (prescription.doctor) {
        return `Prescription #${prescription.id} - Dr. ${prescription.doctor.user_name}`;
      }
      return `Prescription #${prescription.id}`;
    },

    getProductDisplayText(product) {
      if (product.category) {
        return `${product.name} (${product.category})`;
      }
      return product.name;
    },

    async updatePrescriptionItem() {
      this.isSaving = true;
      this.errors = {};

      try {
        const prescriptionItemData = {
          prescription_id: this.form.prescription_id,
          product_id: this.form.product_id,
          mode_id: this.form.mode_id,
          dosage: this.form.dosage,
          frequency: this.form.frequency,
          duration: this.form.duration,
          notes: this.form.notes,
          is_active: this.form.is_active,
        };

        const prescriptionItemId = this.$route.params.id;
        const response = await axios.patch(
          `/prescription_items/${prescriptionItemId}`,
          prescriptionItemData
        );

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.prescriptionItem = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while updating the prescription item.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while updating the prescription item.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deletePrescriptionItem() {
      this.isDeleting = true;

      try {
        const prescriptionItemId = this.$route.params.id;
        const response = await axios.post(
          `/prescription_items/delete/${prescriptionItemId}`
        );

        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push("/prescription-items");
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while deleting the prescription item.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the prescription item.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}

.v-text-field.solo .v-input__control {
  min-height: 48px;
}

.v-textarea.solo .v-input__control {
  min-height: 120px;
}
</style>